/**
 * `auto_number` → **Settings** (spec M2 tier-2 §5.14, card P-123).
 *
 * `auto_number` is the one type monday returned **no cell for at all** — *"values cannot be read,
 * filtered, updated, or cleared because they are computed client-side"* `[09§A.7 auto_number]` —
 * so everything visible about it is settings. `autoNumberSettingsSchema` mirrors the one observed
 * blob: `scope` (`"board"`), `prefix`, `order` (`ascending` | `descending`), `font` and `color`.
 *
 * `scope` and `font` are round-tripped but **not offered**: only `"board"` was ever observed for
 * the first (a second scope would change what the number counts, and inventing one would change
 * every cell), and no font vocabulary was captured for the second. q524.
 */
import type { AutoNumberSettings } from '@elizmonday/shared';
import {
  ColorSwatches,
  HideSummaryField,
  SegmentedField,
  SettingsField,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

const ORDER = [
  { id: 'ascending', label: 'Ascending', value: 'ascending' },
  { id: 'descending', label: 'Descending', value: 'descending' },
] as const;

export function AutoNumberSettingsDialog({
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<AutoNumberSettings>) {
  const [draft, patch] = useSettingsDraft<AutoNumberSettings>(settings);
  return (
    <SettingsForm
      title="Auto Number column settings"
      testId="auto-number-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SettingsField
        label="Prefix"
        testId="auto-number-prefix"
        value={draft.prefix ?? ''}
        placeholder="None"
        maxLength={16}
        onChange={(next) => patch({ prefix: next })}
      />
      <SegmentedField
        label="Order"
        testId="auto-number-order"
        value={draft.order ?? 'ascending'}
        options={ORDER}
        onChange={(next) => patch({ order: next })}
      />
      <ColorSwatches
        testId="auto-number-color"
        value={draft.color}
        onChange={(hex) => patch({ color: hex })}
      />
      <HideSummaryField
        testId="auto-number-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
